import { reactive } from 'vue'
import { storeToRefs } from 'pinia'
import Jet, { trackDynamic } from '@/modules/Jet'
import { JET_SCREEN_EVENT_NAMES } from '@/composables/jetScreenEventNames'
import { featureFlags as localFeatureFlags } from '@/modules/Onboarding/runtime/external/featureFlags'
import { useUserFlagsStore } from '@/store/userFlags'
import {
	STEP_TYPE,
	POPUP_CLOSE_TYPE,
	formatTitle,
	jetGenerateStepId,
	jetComputeSecondsOnScreen,
	persistStepState,
	readPersistedStepState,
} from '@/composables/stepEventsCore'

// Explicit stepType from the builder config is a string; the wire enum is numeric.
const STEP_TYPE_BY_NAME = {
	question: STEP_TYPE.QUESTION,
	loader: STEP_TYPE.LOADER,
	confirm: STEP_TYPE.CONFIRM,
	other: STEP_TYPE.OTHER,
}

const QUESTION_COMPONENT_TYPES = new Set([
	'listInput',
	'checkbox',
	'textInput',
	'dateInput',
	'numberInput',
	'imageInput',
	'rateInput',
	'voteInput',
	'tagsInput',
])

// Canvas loader components (builder catalog name + the one used in real configs).
const CANVAS_LOADER_COMPONENTS = new Set(['ProgressLoader', 'ProcessingLoader'])

// Canonical button names for OnboardingButtonClick event
const BUTTON_NAME = {
	PRIVACY_POLICY: 'privacy_policy',
	TERMS_OF_USE: 'terms_of_use',
	LANGUAGE_SELECTOR: 'language_selector',
	SYSTEM_OF_MEASUREMENT: 'system_of_measurement',
	NEXT: 'NEXT',
	BACK: 'BACK',
}

// Event names emitted by custom components via $emit('event-triggered', { name })
const CUSTOM_EVENT_NAME = {
	HEIGHT_FORM_ERROR: 'HeightFormError',
}

// href pattern → button_name for DOM-delegated OnboardingButtonClick tracking.
// Only links whose href matches one of these patterns trigger the event.
const LINK_HREF_TO_BUTTON = [
	{ pattern: /\/privacy/, name: BUTTON_NAME.PRIVACY_POLICY },
	{ pattern: /\/tos\b|\/terms/, name: BUTTON_NAME.TERMS_OF_USE },
]

// Renderer stamps autotest data-attrs on real nav buttons (canvas: engine/dataAttrs
// rules; legacy: inline attrs). Auto-advance, keyboard and browser-back never hit
// these elements, so a match here is a genuine click. DEV-32329.
const NAV_BUTTON_SELECTOR =
	'[data-button-type="nav-next"], [data-button-type="nav-back"], [data-button-type="top-nav-back"]'

// Screen-specific Jet event names (from PR #2, request_id=821).
// List lives in jetScreenEventNames.ts with a `satisfies EventName[]` compile-time guard.
// Events in this set are forwarded to Jet via jetScreenSpecificEvent().
const JET_SCREEN_EVENTS = new Set(JET_SCREEN_EVENT_NAMES)

// Property-level type conversions for screen-specific events.
// Properties not listed here are passed as-is (strings, booleans).
const JET_PROPERTY_TYPE = {
	height: 'decimal',
	weight: 'decimal',
	year: 'long',
	secondsOnScreen: 'long',
	date: 'timestamp',
}

// Legacy CMS analyticsKey → Jet screen-specific event name + value property key.
// The renderer fires these via onHandleComponentEvents (onUnmounted) with { data: componentValue }.
// handleCustomAnalyticEvent in OnboardingView uses this map to forward data to jetScreenSpecificEvent.
const LEGACY_TO_JET_EVENT = {
	OnboardingHeightScreenDone: { jetName: 'OnboardingHeightSelectionDone', valueKey: 'height' },
	OnboardingWeightScreenDone: { jetName: 'OnboardingWeightSelectionDone', valueKey: 'weight' },
	OnboardingWeightGoalScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
	OnboardingHappyWeightScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
}

export {
	STEP_TYPE,
	BUTTON_NAME,
	POPUP_CLOSE_TYPE,
	JET_SCREEN_EVENTS,
	LEGACY_TO_JET_EVENT,
	CUSTOM_EVENT_NAME,
	jetGenerateStepId,
	jetComputeSecondsOnScreen,
}

// ─── Pure helpers (no state/store dependency) ────────────────────────────────

function jetIsConditional(screen) {
	const conditions = screen?.navigationParams?.conditionsForShow ?? []
	return conditions.some((c) => !(c.variableName === 'featureKey' && /^web_/.test(c.value)))
}

// Heuristic table lives in the DEV-32329 spec and is mirrored by the builder's
// canvas-step-type migration/codemod — keep the two in sync.
function canvasTreeSome(nodes, predicate) {
	const stack = [...(nodes ?? [])]
	while (stack.length) {
		const node = stack.pop()
		if (!node || typeof node !== 'object') continue
		if (predicate(node)) return true
		for (const slot of node.slots ?? []) {
			for (const child of slot.children ?? []) stack.push(child)
		}
	}
	return false
}

function jetComputeCanvasStepType(nodes) {
	if (canvasTreeSome(nodes, (n) => CANVAS_LOADER_COMPONENTS.has(n.component))) return STEP_TYPE.LOADER
	if (canvasTreeSome(nodes, (n) => n.props?.variable != null)) return STEP_TYPE.QUESTION
	if (canvasTreeSome(nodes, (n) => n.component === 'Button')) return STEP_TYPE.CONFIRM
	return STEP_TYPE.OTHER
}

function jetComputeStepType(screen) {
	const explicit = screen?.stepType
	if (typeof explicit === 'number') return explicit
	if (typeof explicit === 'string' && STEP_TYPE_BY_NAME[explicit] != null) return STEP_TYPE_BY_NAME[explicit]

	if (Array.isArray(screen?.nodes) && screen.nodes.length > 0) return jetComputeCanvasStepType(screen.nodes)

	const allComponents = [...(screen?.components ?? []), ...Object.values(screen?.bottomBlock ?? {})]
	const ids = allComponents.map((c) => c.id)

	if (ids.some((id) => QUESTION_COMPONENT_TYPES.has(id))) return STEP_TYPE.QUESTION
	if (ids.includes('button')) return STEP_TYPE.CONFIRM
	if (allComponents.some((c) => c.id === 'custom' && /loader/i.test(c.name))) return STEP_TYPE.LOADER

	return STEP_TYPE.OTHER
}

function jetResolveButtonNameFromHref(href) {
	if (!href) return null
	for (const { pattern, name } of LINK_HREF_TO_BUTTON) {
		if (pattern.test(href)) return name
	}
	return null
}

// ─── Composable ──────────────────────────────────────────────────────────────

export function useOnboardingStepEvents() {
	const { getFlags: getUserFlags } = storeToRefs(useUserFlagsStore())

	const lastStep = readPersistedStepState()
	const stepEvents = reactive({
		currentStepId: lastStep?.stepId || null,
		previousStepId: null,
		currentTitle: lastStep?.title || '',
		previousTitle: '',
		showTimestamp: null,
		isBackwards: false,
		isConditional: false,
		isControl: true,
		isVariation: false,
		stepType: STEP_TYPE.OTHER,
		lastAnswerData: null,
		lastSecondsOnScreen: null,
		_popupStepId: null,
		_popupTitle: null,
		_buttonClickStepId: null,
		_buttonClickTitle: null,
	})

	// ─── Store-dependent helpers ─────────────────────────────────────────

	function jetIsAbsmartlyCondition(value) {
		if (value in localFeatureFlags) return false
		if (value === getUserFlags.value?.local?.funnelFeatureFlag) return false
		return true
	}

	function jetIsControl(screen) {
		const conditions = screen?.navigationParams?.conditionsForShow ?? []
		return !conditions.some((c) => c.variableName === 'featureKey' && jetIsAbsmartlyCondition(c.value))
	}

	// ─── Public API ─────────────────────────────────────────────────────

	function jetStepShow(screen, isBackwards, isVariation) {
		const title = formatTitle(screen?.name || '')
		const ts = Date.now()

		// Variation screens (config __variation.parentId → renderer __isVariation) are
		// mutually exclusive with is_conditional — mirrors the renderer's own
		// isConditionScreen = !isVariation && realConditions.length > 0. DEV-32329.
		const isConditional = !isVariation && jetIsConditional(screen)
		const isControl = jetIsControl(screen)
		const stepType = jetComputeStepType(screen)
		const stepId = jetGenerateStepId(title)

		stepEvents.previousStepId = stepEvents.currentStepId
		stepEvents.previousTitle = stepEvents.currentTitle
		stepEvents.currentStepId = stepId
		stepEvents.currentTitle = title
		stepEvents.showTimestamp = ts
		persistStepState(stepId, title)
		stepEvents.isBackwards = isBackwards
		stepEvents.isConditional = isConditional
		stepEvents.isControl = isControl
		stepEvents.isVariation = !!isVariation
		stepEvents.stepType = stepType
		stepEvents.lastAnswerData = null

		Jet.track('OnboardingStepShow', {
			onboarding_step_id: stepId,
			...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
			onboarding_title: title,
			...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
			is_backwards: isBackwards,
			is_control: isControl,
			is_conditional: isConditional,
			is_variation: !!isVariation,
			step_type: stepType,
		})
	}

	function jetStepDone(isStepBack) {
		if (!stepEvents.showTimestamp) return

		const secondsOnScreen = jetComputeSecondsOnScreen(stepEvents.showTimestamp)

		stepEvents.lastSecondsOnScreen = secondsOnScreen

		// Per spec 8.15: when user leaves via back, data_* must be NULL
		const answer = isStepBack ? {} : stepEvents.lastAnswerData || {}

		Jet.track('OnboardingStepDone', {
			onboarding_step_id: stepEvents.currentStepId,
			...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
			onboarding_title: stepEvents.currentTitle,
			...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
			is_backwards: stepEvents.isBackwards,
			is_control: stepEvents.isControl,
			is_conditional: stepEvents.isConditional,
			is_variation: stepEvents.isVariation,
			step_type: stepEvents.stepType,
			...(secondsOnScreen !== null && { seconds_on_screen: secondsOnScreen }),
			is_step_back: isStepBack,
			...(answer.data_string != null && { data_string: answer.data_string }),
			...(answer.data_float != null && { data_float: String(answer.data_float) }),
			...(answer.data_int != null && { data_int: answer.data_int }),
			data_array: answer.data_array ?? [],
		})
	}

	function jetPopupShow(modal) {
		// Snapshot stepId/title — the renderer fires screen-changed (next screen)
		// before modal-closed, which would overwrite currentStepId.
		stepEvents._popupStepId = stepEvents.currentStepId
		stepEvents._popupTitle = stepEvents.currentTitle

		Jet.track('OnboardingPopupShow', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(modal?.name && { popup_title: modal.name }),
		})
	}

	function jetPopupDone(modal, closeType = POPUP_CLOSE_TYPE.OTHER) {
		const stepId = stepEvents._popupStepId || stepEvents.currentStepId
		const title = stepEvents._popupTitle || stepEvents.currentTitle

		Jet.track('OnboardingPopupDone', {
			...(stepId && { onboarding_step_id: stepId }),
			...(title && { onboarding_title: title }),
			...(modal?.name && { popup_title: modal.name }),
			close_type: closeType,
		})

		stepEvents._popupStepId = null
		stepEvents._popupTitle = null
	}

	function jetButtonClick(buttonName) {
		// Attribute to the LEAVING screen. A nav click bubbles up to our delegated
		// handler only AFTER the renderer's own button handler has synchronously
		// navigated — advancing currentStepId/currentTitle to the ENTERED screen.
		// jetSnapshotDelegatedClick pins the leaving screen in capture phase (before
		// that navigation); prefer it, falling back to live current for programmatic
		// calls that never went through a DOM click. DEV-32329.
		const stepId = stepEvents._buttonClickStepId || stepEvents.currentStepId
		const title = stepEvents._buttonClickTitle || stepEvents.currentTitle

		Jet.track('OnboardingButtonClick', {
			...(stepId && { onboarding_step_id: stepId }),
			...(title && { onboarding_title: title }),
			...(buttonName && { button_name: buttonName }),
		})

		// Consume the snapshot so it can never leak into an unrelated later click —
		// true because the capture gate below only sets it when this handler will
		// actually consume it (same nav-button-or-resolvable-anchor condition).
		stepEvents._buttonClickStepId = null
		stepEvents._buttonClickTitle = null
	}

	// Capture-phase companion to jetHandleDelegatedClick. Runs on the builder root
	// while the click is still descending toward the button — i.e. BEFORE the
	// button's own handler navigates — so currentStepId/currentTitle still hold the
	// screen the user is leaving. The gate here MUST mirror jetHandleDelegatedClick's
	// own condition (nav button, or anchor whose href resolves via
	// jetResolveButtonNameFromHref) — any broader match would snapshot for a click
	// jetButtonClick never fires for (e.g. an unrelated <a>), leaving a stale
	// snapshot to leak into the next real button click. DEV-32329.
	function jetSnapshotDelegatedClick(event) {
		// Synthetic dispatch (extensions, automation) can target a non-Element
		// (e.g. a Text node) that has no closest().
		if (!(event.target instanceof Element)) return
		const navButton = event.target.closest(NAV_BUTTON_SELECTOR)
		const anchor = navButton ? null : event.target.closest('a')
		if (!navButton && !(anchor && jetResolveButtonNameFromHref(anchor.href))) return
		stepEvents._buttonClickStepId = stepEvents.currentStepId
		stepEvents._buttonClickTitle = stepEvents.currentTitle
	}

	function jetHandleDelegatedClick(event) {
		if (!(event.target instanceof Element)) return
		const navButton = event.target.closest(NAV_BUTTON_SELECTOR)
		if (navButton) {
			jetButtonClick(navButton.dataset.buttonType === 'nav-next' ? BUTTON_NAME.NEXT : BUTTON_NAME.BACK)
			return
		}

		const el = event.target.closest('a')
		if (!el) return

		const buttonName = jetResolveButtonNameFromHref(el.href)
		if (buttonName) {
			jetButtonClick(buttonName)
		}
	}

	function jetAlertDone(alertDescription, data) {
		Jet.track('OnboardingAlertDone', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(alertDescription && { alert_description: alertDescription }),
			...(data != null && { data }),
		})
	}

	/**
	 * Forwards a renderer-emitted screen-specific event to Jet.
	 * Adds onboardingStepId automatically. onboardingTitle is NOT added —
	 * only a few events have it in their schema (e.g. OnboardingHealthProblemRiskDone).
	 * Callers must pass onboardingTitle explicitly in properties when needed.
	 * Unknown events (not in JET_SCREEN_EVENTS) are silently ignored.
	 */
	function jetScreenSpecificEvent(eventName, properties = {}) {
		if (!JET_SCREEN_EVENTS.has(eventName)) return

		const converted = {}
		if (stepEvents.currentStepId) {
			converted.onboardingStepId = stepEvents.currentStepId
		}

		for (const [key, value] of Object.entries(properties || {})) {
			if (key === 'data') continue // raw answer — already captured by jetCaptureAnswer
			if (value == null) continue

			const type = JET_PROPERTY_TYPE[key]
			if (type === 'decimal') {
				// Keep a NUMBER (not String): the decimal wire field is a protobuf `string`, but the SDK's
				// BinaryWriter.string() coerces number→string byte-identically (`.string(150)` === `.string("150")`),
				// so the wire stays unchanged while the dev-console log shows a number — matching the old Jet.js
				// #logger, which normalised the Decimal to a number for display (Decimal.toNumber()).
				converted[key] = Number(value)
			} else if (type === 'long') {
				converted[key] = value
			} else if (type === 'timestamp') {
				converted[key] = new Date(value).getTime()
			} else {
				converted[key] = value
			}
		}

		// OnboardingWelcomeDone carries secondsOnScreen for the landing loader. Timing
		// (DEV-34114, stand-verified): the pilot fires this via onLandingWelcomeDone on the
		// loader's ds:leave, which the renderer emits BEFORE @screen-changed (→ jetStepDone
		// sets lastSecondsOnScreen), so lastSecondsOnScreen is the PREVIOUS step's — compute
		// live from the current step's showTimestamp. lastSecondsOnScreen is a defensive
		// fallback for a missing showTimestamp. Raw number (analytics-ts, no Long — DEV-33343).
		if (eventName === 'OnboardingWelcomeDone' && !converted.secondsOnScreen) {
			const seconds = stepEvents.showTimestamp
				? jetComputeSecondsOnScreen(stepEvents.showTimestamp)
				: stepEvents.lastSecondsOnScreen
			if (seconds != null) {
				converted.secondsOnScreen = seconds
			}
		}

		// Dynamic event name (from JET_SCREEN_EVENTS) → trackDynamic snakes keys + tracks.
		trackDynamic(eventName, converted)
	}

	// Called from surveyEventHandler when a *ScreenNameDone* event fires
	function jetCaptureAnswer(data) {
		const raw = data?.properties?.data
		if (raw === undefined || raw === null) return

		// Compound data: {data_string: 'cm', data_float: 175}
		if (typeof raw === 'object' && !Array.isArray(raw)) {
			stepEvents.lastAnswerData = raw
			return
		}

		if (Array.isArray(raw)) {
			stepEvents.lastAnswerData = { data_array: [...raw].map(String).sort() }
		} else if (typeof raw === 'number') {
			if (Number.isInteger(raw)) {
				stepEvents.lastAnswerData = { data_int: raw }
			} else {
				stepEvents.lastAnswerData = { data_float: raw }
			}
		} else if (typeof raw === 'string') {
			stepEvents.lastAnswerData = { data_string: raw }
		}
	}

	return {
		stepEvents,
		jetStepShow,
		jetStepDone,
		jetPopupShow,
		jetPopupDone,
		jetButtonClick,
		jetHandleDelegatedClick,
		jetSnapshotDelegatedClick,
		jetAlertDone,
		jetScreenSpecificEvent,
		jetCaptureAnswer,
	}
}
